import styles from "../UserAdminModal.module.css";
import { useUserAdminContext } from "../../context/UserAdminContext";
import { buildImageSrc, canProfessorManageStudent, getRoleBadgeClass, ROLE_LABELS } from "./userAdminHelpers";

const ORG_ROLE_LABELS = { owner: "Dono", manager: "Gestor", professor: "Professor" };

// Extraído de UserAdminModal.jsx (issue 012, docs/07-issues.md) — aba "Lista"
// (busca + cards de usuários). Comportamento idêntico, só mudou de arquivo;
// estado continua todo em UserAdminModal.jsx, consumido via useUserAdminContext().
export default function UsersListTab() {
  const {
    currentUser,
    apiBase,
    searchQuery,
    setSearchQuery,
    visibleUsers,
    saving,
    startEdit,
    onClickDelete,
    runAction,
    handleUpdateUser,
    setConfirmMessage,
    setConfirmLabel,
    confirmActionRef,
    setShowConfirm,
    userLabels,
    usersHasMore,
    usersRemaining,
    loadMoreUsers,
    loadingMoreUsers,
  } = useUserAdminContext();

  return (
    <div className={styles.uamList}>
      <div className={`${styles.uamField} ${styles.uamSearchSticky}`}>
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por nome ou email…"
          aria-label="Buscar usuários"
        />
      </div>
      {visibleUsers.map((u) => (
        <div key={u.id} className={styles.uamUserCard} role="button" tabIndex={saving ? -1 : 0} onClick={() => !saving && startEdit(u)} onKeyDown={(e) => { if (!saving && (e.key === "Enter" || e.key === " ")) startEdit(u); }} title="Clique para editar" aria-disabled={saving}>
          <div className={styles.uamUserMain}>
            <div className={styles.uamAvatar}>
              {u.avatar_url ? (
                <img src={buildImageSrc(u.avatar_url, apiBase)} alt={u.name} loading="lazy" referrerPolicy="no-referrer" />
              ) : (
                <span>{u.name?.charAt(0)?.toUpperCase()}</span>
              )}
            </div>

            <div className={styles.uamUserMeta}>
              <h4>{u.name}</h4>
              <p>{u.email}</p>

              <div className={styles.uamBadges}>
                <span className={`${styles.uamBadge} ${getRoleBadgeClass(u.role)}`}>
                  {ROLE_LABELS[u.role]}
                </span>

                {/* Professor vê de quem é o aluno: dele, de um colega da
                    academia ou de ninguém (fila "Sem professor"). */}
                {currentUser.role === "professor" && u.role === "aluno" ? (
                  u.professor_id === currentUser.id ? (
                    <span className={`${styles.uamBadge} ${styles.badgeMine}`}>Meu aluno</span>
                  ) : u.professor_id ? (
                    <span className={`${styles.uamBadge} ${styles.muted}`}>
                      Prof. {u.professor_name || `#${u.professor_id}`}
                    </span>
                  ) : (
                    <span className={`${styles.uamBadge} ${styles.badgeNoProfessor}`}>Sem professor</span>
                  )
                ) : u.professor_id && (
                  <span className={`${styles.uamBadge} ${styles.muted}`}>
                    Professor: {u.professor_name || `#${u.professor_id}`}
                  </span>
                )}

                {u.organization_name && currentUser.role === "admin" && (
                  <span className={`${styles.uamBadge} ${styles.muted}`}>
                    {u.organization_name}
                    {u.org_role ? ` · ${ORG_ROLE_LABELS[u.org_role] || "Professor"}` : ""}
                  </span>
                )}

                {u.is_active === false && (
                  <span className={`${styles.uamBadge} ${styles.badgeInactive}`}>
                    Desativado
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.uamActions}>
            {(currentUser.role === "admin" ||
              (currentUser.role === "professor" && canProfessorManageStudent(currentUser, u))) && (
              <>
                <button
                  type="button"
                  className={`${styles.uamBtn} ${styles.ghost}`}
                  onClick={(e) => { e.stopPropagation(); startEdit(u); }}
                  disabled={saving}
                >
                  Editar
                </button>

                {u.id !== currentUser.id && currentUser.role === "admin" && (
                  <button
                    type="button"
                    className={`${styles.uamBtn} ${styles.danger}`}
                    onClick={(e) => { e.stopPropagation(); onClickDelete(u.id); }}
                    disabled={saving}
                  >
                    Excluir
                  </button>
                )}

                {u.id !== currentUser.id && currentUser.role === "professor" && (
                  <button
                    type="button"
                    className={`${styles.uamBtn} ${u.is_active ? styles.danger : styles.ghost}`}
                    onClick={(e) => { e.stopPropagation();
                      setConfirmMessage(u.is_active ? `Deseja inativar ${u.name}?` : `Deseja reativar ${u.name}?`);
                      setConfirmLabel(u.is_active ? "Inativar" : "Reativar");
                      confirmActionRef.current = async () => {
                        await runAction(
                          () => handleUpdateUser(u.id, { is_active: !u.is_active }),
                          u.is_active ? "Usuário inativado." : "Usuário reativado."
                        );
                      };
                      setShowConfirm(true);
                    }}
                    disabled={saving}
                  >
                    {u.is_active ? "Inativar" : "Reativar"}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      {usersHasMore && (
        <button
          type="button"
          className={`${styles.uamBtn} ${styles.ghost} ${styles.uamLoadMore}`}
          onClick={loadMoreUsers}
          disabled={loadingMoreUsers}
        >
          {loadingMoreUsers ? "Carregando..." : `Carregar mais (${usersRemaining} restantes)`}
        </button>
      )}

      {!visibleUsers.length && !usersHasMore && (
        <div className={styles.uamEmpty}>
          {userLabels.list}
        </div>
      )}
    </div>
  );
}
